/* eslint-disable @typescript-eslint/no-explicit-any */
import GenericCard from "@ui-components/Card/GenericCard";
import SearchSelect from "@ui-components/Select/GenericSelect";
import { ReactComponent as SearchIcon } from "@shared/assets/Icons/IconsSearch.svg";
import React, { useEffect, useMemo, useState } from "react";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import {
  customerColumns as baseColumns,
  CustomerNotificationSettings,
} from "./CustomerSettingsData";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import {
  fetchCustomerSettings,
  notificationModuleEnabled,
} from "../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";

export default function CustomerSettings() {
  // Table data state
  const [customerSettings, setCustomerSettings] = useState<
    CustomerNotificationSettings[]
  >([]);
  const api = useAxiosAuth0();
  const { partnerId, tenantName } = useAppSelector(
    (state) => state.selection
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  //Don’t show this component if the user is a CUSTOMER
  if (tenantName === "CUSTOMER") {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filter, setFilter] = useState<string>("All");
  // Modal state
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [activeCustomer, setActiveCustomer] =
    useState<CustomerNotificationSettings | null>(null);
  // Filtered data
  const filteredData = useMemo(() => {
    if (filter === "All") return customerSettings;
    return customerSettings.filter((c) => c.customerName === filter);
  }, [filter, customerSettings]);

  useEffect(() => {
    if (tenantName === "CUSTOMER") return;
    //Don’t run until we know the partner
    if (!partnerId) return;

    const fetchData = async () => {
      try {
        const response = await fetchCustomerSettings(api, partnerId);
        const customers: any[] = response.data?.data || [];

        const formatted: CustomerNotificationSettings[] = customers.map(
          (c) => ({
            tenantId: c.id,
            customerId: c.customerId.toString() ?? "-",
            customerName: c.nameDisplay || c.name,
            notificationModuleEnabled: c.canAccessAlerts,
            enabledNotifications: [],
            recipients: [],
          })
        );
        setCustomerSettings(formatted);
      } catch (e) {
        console.error("Failed fetching customer settings:", e);
      }
    };

    fetchData();
  }, [api, partnerId, tenantName, refreshCounter]);

  // Override Notification Module column
  const columns = useMemo<GenericTableColumn<CustomerNotificationSettings>[]>(
    () =>
      baseColumns.map((col) => {
        if (col.header === "Notification Module") {
          return {
            ...col,
            renderCell: (row: CustomerNotificationSettings) => {
              const enabled = row.notificationModuleEnabled;
              return (
                <GenericTag
                  variant={enabled ? "success" : "error"}
                  label={enabled ? "Enabled" : "Disabled"}
                  size="small"
                  rounded="full"
                  className="cursor-pointer"
                  onClick={() => {
                    setActiveCustomer(row);
                    enabled
                      ? setShowDisableModal(true)
                      : setShowEnableModal(true);
                  }}
                />
              );
            },
          };
        }
        return col;
      }),
    [baseColumns]
  );

  const filterOptions = useMemo(
    () => [
      { value: "All", label: "All Customers" },
      ...customerSettings.map((c) => ({
        value: c.customerName,
        label: c.customerName,
      })),
    ],
    [customerSettings]
  );

  // Confirmation handlers
  const handleConfirmEnable = async () => {
    if (!activeCustomer) return;
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const resp = await notificationModuleEnabled(
        api,
        activeCustomer.tenantId,
        true
      );

      //If there's an error, show it in the modal
      if (!resp.data.success) {
        setErrorMessage(resp.data.message || "Unknown error");
        return;
      }

      //On true success, close and refetch
      setShowEnableModal(false);
      setActiveCustomer(null);
      setRefreshCounter((r) => r + 1);
    } catch (err) {
      console.error(err);
      setErrorMessage(
        "There has been a network error — please try again in a moment."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmDisable = async () => {
    if (!activeCustomer) return;
    setIsProcessing(true);

    try {
      const resp = await notificationModuleEnabled(
        api,
        activeCustomer.tenantId,
        false
      );

      if (!resp.data.success) {
        setErrorMessage(resp.data.message || "Unknown error");
        return;
      }

      setShowDisableModal(false);
      setActiveCustomer(null);
      setRefreshCounter((r) => r + 1);
    } catch (err) {
      console.error(err);
      setErrorMessage(
        "There has been a network error — please try again in a moment."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div>
      <GenericCard
        headerContent="Customer Settings"
        isCollapsible={false}
        bodyContent={
          <>
            <div className="pb-5 space-y-4">
              <SearchSelect
                options={filterOptions}
                defaultValue={filter}
                onSelect={(val: string) => setFilter(val)}
                label="Filter by customer"
                leftIcon={<SearchIcon className="w-5 h-5 text-gray-400" />}
              />
            </div>
            <GenericTable
              columns={columns}
              data={filteredData}
              initialRowsPerPage={5}
              rowsPerPageOptions={[5, 10]}
              width="100%"
              tableClassName="bg-semantic-bg-primary"
            />
          </>
        }
      />
      {/* Enable Modal */}
      <GenericModal
        isOpen={showEnableModal}
        onClose={() => setShowEnableModal(false)}
        title="Enable Notification Module"
      >
        {errorMessage && (
          <div
            className={
              "p-2 mb-2 rounded text-sm border bg-red-50 border-red-200 text-red-700"
            }
          >
            {errorMessage}
          </div>
        )}
        <div className="my-10 py-5 px-3 bg-[var(--color-primitive-gray-50)] border rounded-lg">
          <p className="text-sm">
            You are about to enable the notification module for{" "}
            <strong>{activeCustomer?.customerName}</strong>. This will grant
            them access to customize their notification settings.
          </p>
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <GenericButton
            variant="outlined"
            onClick={() => {
              setShowEnableModal(false);
              setErrorMessage(null);
            }}
            label="Cancel"
            disabled={isProcessing}
          />
          <GenericButton
            variant="brand"
            onClick={handleConfirmEnable}
            label="Confirm"
            loading={isProcessing}
            disabled={isProcessing}
          />
        </div>
      </GenericModal>

      {/* Disable Modal */}
      <GenericModal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        title="Disable Notification Module"
      >
        {errorMessage && (
          <div
            className={
              "p-2 mb-2 rounded text-sm border bg-red-50 border-red-200 text-red-700"
            }
          >
            {errorMessage}
          </div>
        )}
        <div className="my-10 py-5 px-3 bg-[var(--color-primitive-gray-50)] border rounded-lg">
          <p className="text-sm">
            Are you sure you want to disable the notification module for{" "}
            <strong>{activeCustomer?.customerName}</strong>? This will revoke
            their access to the notification dashboard and prevent further
            customization.
          </p>
        </div>
        <div className="flex justify-end space-x-2">
          <GenericButton
            size="sm"
            variant="outlined"
            onClick={() => {
              setShowDisableModal(false);
              setErrorMessage(null);
            }}
            label="Cancel"
            disabled={isProcessing}
          />
          <GenericButton
            size="sm"
            variant="brand"
            onClick={handleConfirmDisable}
            label="Confirm"
            disabled={isProcessing}
            loading={isProcessing}
          />
        </div>
      </GenericModal>
    </div>
  );
}
